import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { useI18n } from "../i18n/I18nContext";
import { useSmoothScroll } from "./SmoothScroll";

export default function Header() {
  const { t } = useI18n();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollTo, scrollToTop } = useSmoothScroll();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const home = `/${lang || "ar"}`;
  const isHome = location.pathname === home;
  const overHero = isHome && !scrolled;
  const solid = scrolled || !isHome;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (id) => {
    setOpen(false);
    if (location.pathname === home) {
      scrollTo(`#${id}`);
    } else {
      navigate(home);
      setTimeout(() => scrollTo(`#${id}`), 550);
    }
  };

  const goHome = () => {
    setOpen(false);
    if (location.pathname === home) {
      scrollToTop(false);
    } else {
      navigate(home);
    }
  };

  const links = [
    { id: "analysis", key: "nav.analysis" },
    { id: "circuit", key: "nav.circuit" },
    { id: "dashboard", key: "nav.dashboard" },
    { id: "transparency", key: "nav.transparency" },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-sand/85 backdrop-blur-md border-b border-ink/10 shadow-[0_1px_20px_rgba(10,25,47,0.05)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <button
            type="button"
            data-testid="header-logo"
            onClick={goHome}
            className="shrink-0"
            aria-label="Litiodata"
          >
            <Logo light={overHero} />
          </button>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <button
                key={l.id}
                type="button"
                data-testid={`nav-${l.id}`}
                onClick={() => goToSection(l.id)}
                className={`relative text-sm font-medium transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-[2px] after:w-0 after:bg-terracotta after:transition-all after:duration-300 hover:after:w-full ${
                  overHero ? "text-white/80 hover:text-white" : "text-mineral hover:text-ink"
                }`}
              >
                {t(l.key)}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:block">
              <LanguageSwitcher light={overHero} />
            </div>
            <button
              type="button"
              data-testid="header-cta"
              onClick={() => goToSection("contact")}
              className={`hidden md:inline-flex items-center rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 hover:-translate-y-0.5 ${
                overHero
                  ? "bg-white text-ink hover:bg-terracotta hover:text-white"
                  : "bg-ink text-white hover:bg-terracotta"
              }`}
            >
              {t("nav.cta")}
            </button>
            <button
              type="button"
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-lg ${
                overHero ? "text-white hover:bg-white/10" : "text-ink hover:bg-ink/5"
              }`}
              aria-label="Menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-sand/95 backdrop-blur-md border-b border-ink/10"
          >
            <div className="mx-auto max-w-7xl px-5 py-5 flex flex-col gap-1">
              <button
                type="button"
                data-testid="mobile-nav-home"
                onClick={goHome}
                className="py-3 text-left text-lg font-heading font-semibold text-ink"
              >
                {t("nav.home")}
              </button>
              {links.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => goToSection(l.id)}
                  className="py-3 text-left text-lg font-heading font-semibold text-ink"
                >
                  {t(l.key)}
                </button>
              ))}
              <button
                type="button"
                data-testid="mobile-nav-contact"
                onClick={() => goToSection("contact")}
                className="mt-3 inline-flex items-center justify-center rounded-full bg-ink px-5 py-3 text-base font-semibold text-white"
              >
                {t("nav.cta")}
              </button>
              <div className="mt-4">
                <LanguageSwitcher />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
